// ============================================================================
// E-Sign envelope prep — /money/envelopes/:envelopeId.
// Draft: recipient list (role + signing_order), pdfjs page render, drag-drop
// field palette (@dnd-kit) with per-recipient color coding, reposition by
// dragging placed boxes, nudge-resize on selection, routing-flow preview,
// Send. After send: per-recipient status, signing links (kept in state so a
// refetch can't eat them — the SigningPanel remount bug from the 2026-07-14
// smoke test), advance recovery, and View PDF.
// ============================================================================

import { canViewRestrictedHr } from '../../shared/role-gates';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DndContext, DragEndEvent, PointerSensor, useDraggable, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  ArrowLeft, Ban, Copy, FileText, Loader2, Send, Trash2, UserPlus,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../shared/supabase';
import {
  EnvelopeCopy, EnvStatus, FIELD_DEFAULTS, FieldRow, FieldType, MintResult,
  RecipientRole, envAddRecipient, envAdvance, envDelete, envPlaceField,
  envRemoveField, envSend, envStatus, envVoid, fetchEnvelopePdf,
  recipientColor, RenderedPage, saveEnvelopeCopy,
  EsignTemplate, TemplateField, applyLayout, captureLayout,
  listTemplates, loadPdfPagesAndText, saveLayoutToTemplate, setFieldRequired,
  setRecipientAuthMode,
} from './envelope-lib';
import { EmailComposer } from './email-composer';
import { FieldTools } from './field-tools';
import { AuditTrail } from './audit-trail';
import { RestrictedAnswers } from './restricted-answers';
import { SendConfirm } from './send-confirm';
import { PacketReview } from './packet-review';
import {
  TextRun, findAnchors, isDuplicatePlacement, placementsFromAnchors,
} from './anchor-placement';
import {
  Suggestion, dedupeSuggestions, describeSuggestions, detectFields,
} from './field-detect';
import { errorMessage } from '../../shared/errors';
import { useAuth } from '../../shared/auth';

import { scrollPageTop } from '../../shared/public-page';

const FIELD_TYPES: FieldType[] = [
  'signature', 'initials', 'name', 'email', 'date', 'text', 'checkbox',
];

// ── palette chip (draggable) ────────────────────────────────────────────────

function PaletteChip({ type, color }: { type: FieldType; color: string }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `palette-${type}`,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      {...listeners}
      {...attributes}
      className="cursor-grab touch-none rounded-md border px-2.5 py-1.5 text-xs font-medium text-tcs-text active:cursor-grabbing"
      style={{
        borderColor: color,
        transform: transform
          ? `translate(${transform.x}px, ${transform.y}px)`
          : undefined,
        zIndex: transform ? 50 : undefined,
        position: 'relative',
        backgroundColor: 'white',
      }}
    >
      {FIELD_DEFAULTS[type].label}
    </button>
  );
}

// ── placed field box (draggable within pages) ───────────────────────────────

function FieldBox({
  field, color, pageW, pageH, selected, editable, onSelect,
}: {
  field: FieldRow;
  color: string;
  pageW: number;
  pageH: number;
  selected: boolean;
  editable: boolean;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `field-${field.field_id}`,
    disabled: !editable,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => e.key === 'Enter' && onSelect()}
      className="absolute flex items-center justify-center overflow-hidden rounded-sm border text-[10px] font-medium"
      style={{
        left: field.position_x * pageW,
        top: field.position_y * pageH,
        width: field.width * pageW,
        height: field.height * pageH,
        borderColor: color,
        backgroundColor: `${color}22`,
        color,
        outline: selected ? `2px solid ${color}` : undefined,
        transform: transform
          ? `translate(${transform.x}px, ${transform.y}px)`
          : undefined,
        cursor: editable ? 'move' : 'default',
        touchAction: 'none',
      }}
    >
      <span className="truncate px-0.5">
        {FIELD_DEFAULTS[field.field_type].label}
        {/* The sender needs to see at a glance which fields will block the
            signer's Finish button — the same asterisk the signer sees. */}
        {field.required && <span className="font-bold text-st-danger-fg"> *</span>}
        {/* RESTRICTED. A marker, never the answer: this box collects an SSN or
            a bank account, esign_fields.value is null on it by CHECK, and the
            answer in esign_field_secrets is not read by this screen at all. */}
        {field.is_sensitive === true && (
          <span className="font-semibold"> · restricted</span>
        )}
      </span>
    </div>
  );
}

// ── page component ──────────────────────────────────────────────────────────

function PdfPage({
  page, children, registerRef,
}: {
  page: RenderedPage;
  children: React.ReactNode;
  registerRef: (n: number, el: HTMLDivElement | null) => void;
}) {
  const holder = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = holder.current;
    if (el && page.canvas.parentElement !== el) {
      el.innerHTML = '';
      el.appendChild(page.canvas);
    }
  }, [page]);
  return (
    <div
      ref={(el) => registerRef(page.pageNumber, el)}
      data-page={page.pageNumber}
      className="relative mx-auto mb-4 border border-tcs-border bg-tcs-raised shadow-sm"
      style={{ width: page.width, height: page.height }}
    >
      <div ref={holder} aria-hidden />
      {children}
    </div>
  );
}

// ── main ────────────────────────────────────────────────────────────────────

export function EnvelopePrepPage() {
  const { envelopeId = '' } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();
  // Decides only whether the Restricted answers panel is rendered. The data
  // itself is protected by can_view_restricted_hr() in the DB, not by this.
  const { profile } = useAuth();
  const [data, setData] = useState<EnvStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!envelopeId) return;
    try {
      setData(await envStatus(envelopeId));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'load_failed'));
    } finally {
      setIsLoading(false);
    }
  }, [envelopeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pageRefs = useRef(new Map<number, HTMLDivElement>());
  const registerRef = useCallback((n: number, el: HTMLDivElement | null) => {
    if (el) pageRefs.current.set(n, el);
    else pageRefs.current.delete(n);
  }, []);

  // recipient editor
  const [rEmail, setREmail] = useState('');
  const [rName, setRName] = useState('');
  const [rRole, setRRole] = useState<RecipientRole>('SIGNER');
  const [rOrder, setROrder] = useState(1);
  const [rBusy, setRBusy] = useState(false);
  const [rError, setRError] = useState<string | null>(null);

  const [activeRecipient, setActiveRecipient] = useState<string | null>(null);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Signing links survive refetches on purpose — see header note.
  const [links, setLinks] = useState<MintResult[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Sender-authored email copy + delivery settings (Phase 2).
  const [copy, setCopy] = useState<EnvelopeCopy>({
    email_subject: null,
    email_body: null,
    note_to_signer: null,
    reminder_every_days: null,
    expires_in_days: null,
  });
  const [copySaving, setCopySaving] = useState(false);
  const [copySavedAt, setCopySavedAt] = useState<number | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  // Field tools: required toggle, anchor placement, layout templates.
  const [templates, setTemplates] = useState<EsignTemplate[]>([]);
  const [textRuns, setTextRuns] = useState<TextRun[]>([]);
  const [toolsBusy, setToolsBusy] = useState(false);
  const [toolsMsg, setToolsMsg] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  // Send now goes through a confirmation showing the actual email.
  const [confirmSend, setConfirmSend] = useState(false);
  const [composerOpenSignal, setComposerOpenSignal] = useState(0);

  // AFB-0075 / PR 5 — an envelope assembled from an onboarding packet carries
  // prefilled merge values, and a required one that is still empty must STOP
  // the send rather than warn about it. PacketReview owns the reasons; this
  // page owns the button, so the reasons are reported up to here.
  const [packetBlockers, setPacketBlockers] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const env = data?.envelope;
  const recipients = data?.recipients ?? [];
  const fields = data?.fields ?? [];
  const isDraft = env?.status === 'Draft';
  const signersAndApprovers = recipients.filter((r) => r.role !== 'CC');
  // Only an envelope built from a packet has a review, and only that review
  // can block. Tested against the envelope rather than against the blocker
  // list so a stale list from a previous render can never disable a send that
  // has nothing to do with a packet.
  const isPacketEnvelope = Boolean(env?.packet_id && env?.source_onboarding_id);
  const packetSendBlocked = isPacketEnvelope && packetBlockers.length > 0;

  // Only SIGNERS can hold fields. Approvers review and approve without
  // signing, and CC only receives the completed copy. Fields placed on an
  // approver used to save and then be silently ignored at signing time —
  // see the matching guard in the edge function.
  const placeableRecipients = recipients.filter((r) => r.role === 'SIGNER');
  const activeIsPlaceable = placeableRecipients
    .some((r) => r.recipient_id === activeRecipient);

  useEffect(() => {
    if (!activeRecipient && placeableRecipients.length > 0) {
      setActiveRecipient(placeableRecipients[0].recipient_id);
    }
  }, [activeRecipient, placeableRecipients]);

  const loadPdf = useCallback(async () => {
    setPdfError(null);
    setTextError(null);
    try {
      const url = await fetchEnvelopePdf(envelopeId);
      const targetWidth = Math.min(760, window.innerWidth - 48);
      // ONE document load for both outputs. Opening the same PDF twice —
      // once to render, once to extract text — is the prime suspect for the
      // permanently-greyed "Suggested fields" panel Ben hit on 2026-07-29.
      const { pages: rendered, runs, textError: tErr } =
        await loadPdfPagesAndText(url, targetWidth);
      URL.revokeObjectURL(url);
      setPages(rendered);
      setTextRuns(runs);
      // Say so out loud. The previous version swallowed this and left the
      // sender staring at a disabled button with no explanation.
      if (tErr) setTextError(tErr);
      else if (runs.length === 0) {
        setTextError('no_text_layer');
      }
    } catch {
      setPdfError('Could not render the document.');
    }
  }, [envelopeId]);

  useEffect(() => {
    if (env) void loadPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env?.envelope_id, env?.status === 'Completed']);

  const refresh = () => void load();

  // Hydrate the composer from the envelope whenever it (re)loads.
  useEffect(() => {
    if (!env) return;
    setCopy({
      email_subject: env.email_subject ?? null,
      email_body: env.email_body ?? null,
      note_to_signer: env.note_to_signer ?? null,
      reminder_every_days: env.reminder_every_days ?? null,
      expires_in_days: env.expires_in_days ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env?.envelope_id, env?.updated_at]);

  async function saveCopy() {
    setCopySaving(true);
    setCopyError(null);
    try {
      // Blank strings mean "go back to the default", not "send an empty email".
      await saveEnvelopeCopy(envelopeId, {
        email_subject: copy.email_subject?.trim() || null,
        email_body: copy.email_body?.trim() || null,
        note_to_signer: copy.note_to_signer?.trim() || null,
        reminder_every_days: copy.reminder_every_days,
        expires_in_days: copy.expires_in_days,
      });
      setCopySavedAt(Date.now());
      refresh();
    } catch (e) {
      setCopyError(errorMessage(e, 'Could not save the email.'));
    } finally {
      setCopySaving(false);
    }
  }

  useEffect(() => {
    listTemplates().then(setTemplates).catch(() => { /* non-fatal */ });
  }, []);

  // Text extraction now rides along with the render — see loadPdf. The
  // separate second fetch that used to live here is gone.

  async function toggleRequired(f: FieldRow, required: boolean) {
    setToolsBusy(true);
    setToolsMsg(null);
    try {
      await setFieldRequired(f.field_id, required);
      refresh();
    } catch (e) {
      setSendError(errorMessage(e, 'Could not update the field.'));
    } finally {
      setToolsBusy(false);
    }
  }

  async function placeByAnchor(
    anchorText: string, fieldType: FieldType, required: boolean,
  ) {
    if (!activeRecipient) {
      setToolsMsg('Pick a recipient first — fields belong to somebody.');
      return;
    }
    setToolsBusy(true);
    setToolsMsg(null);
    try {
      const matches = findAnchors(textRuns, anchorText);
      if (matches.length === 0) {
        setToolsMsg(`No match for “${anchorText}” in this document.`);
        return;
      }
      const candidates = placementsFromAnchors(matches, fieldType);
      let placed = 0;
      let skipped = 0;
      for (const p of candidates) {
        // Re-running the same anchor must not stack duplicates.
        if (isDuplicatePlacement(p, fields)) { skipped += 1; continue; }
        await envPlaceField({
          envelope_id: envelopeId,
          recipient_id: activeRecipient,
          field_type: fieldType,
          page: p.page,
          position_x: p.position_x,
          position_y: p.position_y,
          width: p.width,
          height: p.height,
          required,
        });
        placed += 1;
      }
      setToolsMsg(
        `${matches.length} match${matches.length === 1 ? '' : 'es'} · ${placed} placed`
        + (skipped ? ` · ${skipped} already had a field there` : ''),
      );
      refresh();
    } catch (e) {
      setSendError(errorMessage(e, 'Anchor placement failed.'));
    } finally {
      setToolsBusy(false);
    }
  }

  function runDetection() {
    const found = dedupeSuggestions(detectFields(textRuns), fields);
    setSuggestions(found);
    setToolsMsg(describeSuggestions(found));
  }

  async function acceptSuggestions(chosen: Suggestion[]) {
    if (!activeRecipient) {
      setToolsMsg('Pick a recipient first — fields belong to somebody.');
      return;
    }
    setToolsBusy(true);
    try {
      for (const s of chosen) {
        await envPlaceField({
          envelope_id: envelopeId,
          recipient_id: activeRecipient,
          field_type: s.fieldType,
          page: s.page,
          position_x: s.position_x,
          position_y: s.position_y,
          width: s.width,
          height: s.height,
          // A suggestion the matcher was less sure about starts optional, so
          // a wrong guess cannot block the signer from finishing.
          required: s.confidence === 'high',
        });
      }
      setSuggestions(null);
      setToolsMsg(`${chosen.length} suggested field${chosen.length === 1 ? '' : 's'} placed.`);
      refresh();
    } catch (e) {
      setSendError(errorMessage(e, 'Could not place the fields.'));
    } finally {
      setToolsBusy(false);
    }
  }

  async function doApplyLayout(layout: TemplateField[]) {
    setToolsBusy(true);
    setToolsMsg(null);
    try {
      const res = await applyLayout(envelopeId, recipients, layout);
      setToolsMsg(
        `${res.placed} field${res.placed === 1 ? '' : 's'} placed`
        + (res.skipped ? ` · ${res.skipped} skipped (no matching recipient)` : ''),
      );
      refresh();
    } catch (e) {
      setSendError(errorMessage(e, 'Could not apply the layout.'));
    } finally {
      setToolsBusy(false);
    }
  }

  // AFB-0075 / PR 2 — what is captured here is GEOMETRY. A live envelope knows
  // where each box is and who it belongs to; it cannot know whether a box should
  // be prefilled from the database, so captureLayout writes every entry as a
  // signer field with no binding and the message says so. Guessing a binding
  // would put a value nobody chose onto a document somebody then signs.
  async function doSaveLayout(templateId: string) {
    setToolsBusy(true);
    setToolsMsg(null);
    try {
      const layout = captureLayout(recipients, fields);
      await saveLayoutToTemplate(templateId, layout);
      setTemplates(await listTemplates());
      setToolsMsg(
        `Saved ${layout.length} field placement${layout.length === 1 ? '' : 's'} to the ` +
        `template, all as signer fields. Anything the system should prefill has to be ` +
        `tagged on the template itself — "Manage template documents" on the envelope list ` +
        `— before the template can go in a packet.`,
      );
    } catch (e) {
      setSendError(errorMessage(e, 'Could not save the layout.'));
    } finally {
      setToolsBusy(false);
    }
  }

  async function addRecipient() {
    setRBusy(true);
    setRError(null);
    try {
      await envAddRecipient({
        envelope_id: envelopeId,
        email: rEmail.trim(),
        name: rName.trim() || undefined,
        role: rRole,
        signing_order: rRole === 'CC' ? 999 : rOrder,
      });
      setREmail('');
      setRName('');
      refresh();
    } catch (e) {
      setRError(errorMessage(e, 'Add failed.'));
    } finally {
      setRBusy(false);
    }
  }

  /** Find which page element a viewport point is inside. */
  function pageAtPoint(x: number, y: number): { n: number; rect: DOMRect } | null {
    for (const [n, el] of pageRefs.current.entries()) {
      const rect = el.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return { n, rect };
      }
    }
    return null;
  }

  async function onDragEnd(ev: DragEndEvent) {
    const id = String(ev.active.id);
    const translated = ev.active.rect.current.translated;
    if (!translated) return;
    const cx = translated.left + translated.width / 2;
    const cy = translated.top + translated.height / 2;
    const hit = pageAtPoint(cx, cy);

    if (id.startsWith('palette-')) {
      if (!hit || !activeRecipient || !isDraft) return;
      // Belt and braces: the palette is hidden unless a signer is selected,
      // but a drop must never land on an approver either way.
      if (!activeIsPlaceable) {
        setSendError('Fields can only be placed for a Signer.');
        return;
      }
      const type = id.slice(8) as FieldType;
      const def = FIELD_DEFAULTS[type];
      const page = pages.find((p) => p.pageNumber === hit.n);
      if (!page) return;
      const x = Math.min(Math.max((cx - hit.rect.left) / page.width - def.w / 2, 0), 1 - def.w);
      const y = Math.min(Math.max((cy - hit.rect.top) / page.height - def.h / 2, 0), 1 - def.h);
      try {
        await envPlaceField({
          envelope_id: envelopeId,
          recipient_id: activeRecipient,
          field_type: type,
          page: hit.n,
          position_x: Number(x.toFixed(4)),
          position_y: Number(y.toFixed(4)),
          width: def.w,
          height: def.h,
        });
        refresh();
      } catch (e) {
        setSendError(errorMessage(e, 'Field placement failed.'));
      }
      return;
    }

    if (id.startsWith('field-') && isDraft) {
      const fieldId = id.slice(6);
      const f = fields.find((x) => x.field_id === fieldId);
      if (!f || !hit) return;
      const page = pages.find((p) => p.pageNumber === hit.n);
      if (!page) return;
      const x = Math.min(Math.max((cx - hit.rect.left) / page.width - f.width / 2, 0), 1 - f.width);
      const y = Math.min(Math.max((cy - hit.rect.top) / page.height - f.height / 2, 0), 1 - f.height);
      await supabase
        .from('esign_fields')
        .update({
          page: hit.n,
          position_x: Number(x.toFixed(4)),
          position_y: Number(y.toFixed(4)),
        })
        .eq('field_id', fieldId);
      refresh();
    }
  }

  async function resizeSelected(dw: number, dh: number) {
    const f = fields.find((x) => x.field_id === selectedField);
    if (!f) return;
    const width = Math.min(Math.max(f.width + dw, 0.02), 0.9);
    const height = Math.min(Math.max(f.height + dh, 0.012), 0.3);
    await supabase.from('esign_fields').update({ width, height }).eq('field_id', f.field_id);
    refresh();
  }

  async function removeSelected() {
    if (!selectedField) return;
    try {
      await envRemoveField(selectedField);
      setSelectedField(null);
      refresh();
    } catch (e) {
      setSendError(errorMessage(e, 'Remove failed.'));
    }
  }

  async function doSend() {
    // Belt and braces behind the disabled button: a packet with an unresolved
    // required merge field must not go out even if something re-enables the
    // control underneath us.
    if (packetSendBlocked) {
      setSendError(packetBlockers[0]);
      setConfirmSend(false);
      return;
    }
    setSendBusy(true);
    setSendError(null);
    try {
      const res = await envSend(envelopeId);
      setLinks((prev) => [...prev, ...res.minted]);
      setConfirmSend(false);
      refresh();
    } catch (e) {
      setSendError(errorMessage(e, 'Send failed.'));
    } finally {
      setSendBusy(false);
    }
  }

  async function doAdvance(forceRemint = false) {
    setSendBusy(true);
    setSendError(null);
    try {
      const res = await envAdvance(envelopeId, forceRemint);
      setLinks((prev) => [...prev, ...res.minted]);
      refresh();
    } catch (e) {
      setSendError(errorMessage(e, 'Advance failed.'));
    } finally {
      setSendBusy(false);
    }
  }

  async function viewPdf() {
    setPdfBusy(true);
    try {
      const url = await fetchEnvelopePdf(envelopeId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setPdfBusy(false);
    }
  }

  async function voidThis() {
    if (!env) return;
    if (!window.confirm(`Void "${env.title}"? Outstanding signing links stop working immediately.`)) return;
    setSendBusy(true);
    setSendError(null);
    try {
      await envVoid(envelopeId);
      refresh();
    } catch (e) {
      setSendError(errorMessage(e, 'Void failed.'));
    } finally {
      setSendBusy(false);
    }
  }

  async function deleteThis() {
    if (!env) return;
    if (!window.confirm(`Delete "${env.title}" permanently?`)) return;
    setSendBusy(true);
    setSendError(null);
    try {
      await envDelete(envelopeId);
      navigate('/money/envelopes');
    } catch (e) {
      setSendError(errorMessage(e, 'Delete failed.'));
      setSendBusy(false);
    }
  }

  async function copyLink(url: string) {
    await navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 1500);
  }

  if (isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-tcs-text-muted" aria-label="Loading" />
      </div>
    );
  }
  if (error != null || !env) {
    return <p className="p-4 text-sm text-st-danger-fg">Envelope not found.</p>;
  }

  const orders = [...new Set(signersAndApprovers.map((r) => r.signing_order))]
    .sort((a, b) => a - b);
  const ccs = recipients.filter((r) => r.role === 'CC');

  return (
    <div className="p-4">
      <Link
        to="/money/envelopes"
        className="mb-2 inline-flex items-center gap-1 text-sm text-tcs-text-muted hover:text-tcs-text"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> All envelopes
      </Link>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-tcs-text">{env.title}</h2>
          <p className="text-xs text-tcs-text-muted">
            {env.envelope_id} · {env.status.replace('_', ' ')}
            {data?.signing_order != null && env.status !== 'Draft'
              ? ` · waiting on group #${data.signing_order}`
              : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={viewPdf}
            disabled={pdfBusy}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm font-medium text-tcs-text disabled:opacity-50"
          >
            <FileText className="h-4 w-4" aria-hidden />
            {env.status === 'Completed' ? 'Executed PDF' : 'View PDF'}
          </button>
          {isDraft ? (
            <button
              type="button"
              onClick={() => setConfirmSend(true)}
              disabled={sendBusy || signersAndApprovers.length === 0 || packetSendBlocked}
              title={packetSendBlocked ? packetBlockers[0] : undefined}
              aria-describedby={packetSendBlocked ? 'packet-send-blocked' : undefined}
              className="inline-flex min-h-[44px] items-center gap-1 rounded-md bg-tcs-accent-ink px-4 py-2 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
            >
              {sendBusy
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                : <Send className="h-4 w-4" aria-hidden />}
              Send for signature
            </button>
          ) : ['Sent', 'In_Progress'].includes(env.status) ? (
            <>
              <button
                type="button"
                onClick={() => doAdvance(false)}
                disabled={sendBusy}
                className="inline-flex min-h-[44px] items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm font-medium text-tcs-text disabled:opacity-50"
              >
                {sendBusy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                Advance
              </button>
              <button
                type="button"
                onClick={() => doAdvance(true)}
                disabled={sendBusy}
                title="Expires the current group's unclaimed links and issues fresh ones (old links stop working)."
                className="inline-flex min-h-[44px] items-center gap-1 rounded-md border border-st-warn-border-strong bg-st-warn-bg px-3 py-2 text-sm font-medium text-st-warn-fg-strong disabled:opacity-50"
              >
                Re-issue links
              </button>
            </>
          ) : null}
          {['Draft', 'Sent', 'In_Progress'].includes(env.status) && (
            <button
              type="button"
              onClick={voidThis}
              disabled={sendBusy}
              title="Void — outstanding links stop working; the envelope stays listed."
              className="inline-flex min-h-[44px] items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm font-medium text-tcs-text-muted hover:text-st-warn-fg disabled:opacity-50"
            >
              <Ban className="h-4 w-4" aria-hidden /> Void
            </button>
          )}
          {['Draft', 'Voided'].includes(env.status) && (
            <button
              type="button"
              onClick={deleteThis}
              disabled={sendBusy}
              title="Delete permanently (Draft/Voided only — executed documents can't be deleted)."
              className="inline-flex min-h-[44px] items-center gap-1 rounded-md border border-st-danger-border bg-tcs-raised px-3 py-2 text-sm font-medium text-st-danger-fg disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" aria-hidden /> Delete
            </button>
          )}
        </div>
      </div>

      {sendError && (
        <p role="alert" className="mb-3 text-sm text-st-danger-fg">{sendError}</p>
      )}

      {isDraft && packetSendBlocked && (
        <p
          id="packet-send-blocked"
          className="mb-3 text-xs text-st-danger-fg"
        >
          Send is blocked: {packetBlockers[0]}
          {packetBlockers.length > 1
            ? ` (and ${packetBlockers.length - 1} more in the packet review below)`
            : ''}
        </p>
      )}

      {/* ── onboarding-packet review, before anything is sent ────────────
          Only an envelope assembled from a packet has one. Everything a
          hand-built envelope needs is already on this page; what a packet adds
          is prefilled values, resolution gaps, and a send that refuses. */}
      {env.packet_id && env.source_onboarding_id && (
        <PacketReview
          packetId={env.packet_id}
          onboardingId={env.source_onboarding_id}
          documentOverrides={env.document_overrides}
          envelopeTitle={env.title}
          senderName={env.sender_name ?? 'Terminal Cleaning Services'}
          expirationDays={env.expires_in_days ?? 14}
          copy={copy}
          fields={fields}
          recipients={recipients}
          isDraft={isDraft}
          onRefresh={refresh}
          onGateChange={setPacketBlockers}
        />
      )}

      {links.length > 0 && (
        <div className="mb-4 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-3">
          <p className="mb-2 text-xs font-medium text-st-warn-fg-strong">
            {links.some((l) => l.signing_url)
              ? 'Some links could not be emailed — copy those and deliver them yourself. Links are shown once; copy them now. (Hand-delivered links require the email code step at signing.)'
              : 'Signing links emailed. Recipients verify by opening their link — no code step.'}
          </p>
          <ul className="space-y-1.5">
            {links.map((l) => (
              <li key={`${l.recipient_id}-${l.signing_url ?? 'sent'}`} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-st-warn-fg-strong">
                  {l.email}
                  {l.delivery.delivered ? ' — emailed ✓' : ''}
                </span>
                {l.signing_url && (
                  <button
                    type="button"
                    onClick={() => copyLink(l.signing_url!)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-st-warn-border-strong px-2 py-1 font-medium text-st-warn-fg-strong"
                  >
                    <Copy className="h-3 w-3" aria-hidden />
                    {copied === l.signing_url ? 'Copied' : 'Copy link'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── sender-authored email + note to signer ───────────────────────── */}
      <div className="mb-4">
        <EmailComposer
          envelopeTitle={env.title}
          senderName={env.sender_name ?? 'Terminal Cleaning Services'}
          sampleRecipientName={
            signersAndApprovers[0]?.name ?? signersAndApprovers[0]?.email ?? ''
          }
          expirationDays={env.expires_in_days ?? 14}
          value={copy}
          disabled={!isDraft}
          saving={copySaving}
          savedAt={copySavedAt}
          onChange={(patch) => {
            setCopy((c) => ({ ...c, ...patch }));
            setCopySavedAt(null);
          }}
          onSave={saveCopy}
          onPreviewAsSigner={() => navigate(`/money/envelopes/${envelopeId}/preview`)}
          openSignal={composerOpenSignal}
        />
        {copyError && (
          <p role="alert" className="mt-1 text-xs text-st-danger-fg">{copyError}</p>
        )}
        {/* Chain of custody — only meaningful once something has happened. */}
        {!isDraft && (
          <div className="mt-3">
            <AuditTrail envelopeId={envelopeId} />
          </div>
        )}
        {/* Restricted answers. Rendered only for the roles the DB gate allows,
            so the rest of the company never sees a panel it cannot open — but
            the client check is cosmetic: esign_envelope_secrets() refuses on
            its own, and that refusal is what actually protects the data. */}
        {!isDraft && canViewRestrictedHr(profile) && (
          <div className="mt-3">
            <RestrictedAnswers envelopeId={envelopeId} />
          </div>
        )}
      </div>

      {confirmSend && env && (
        <SendConfirm
          envelopeTitle={env.title}
          senderName={env.sender_name ?? 'Terminal Cleaning Services'}
          copy={copy}
          recipients={recipients}
          expirationDays={env.expires_in_days ?? 14}
          busy={sendBusy}
          error={sendError}
          onEditEmail={() => {
            setConfirmSend(false);
            setComposerOpenSignal((n) => n + 1);
            scrollPageTop('smooth');
          }}
          onSend={doSend}
          onClose={() => setConfirmSend(false)}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        {/* ── left rail: recipients + routing + field tools ─────────────────
            Sticky and independently scrollable. It used to scroll away with
            the page, so on a multi-page PDF the recipient picker and the
            field tools vanished exactly when you were placing fields on page
            three (Ben, 2026-07-29). */}
        <div className="space-y-4 lg:sticky lg:top-3 lg:max-h-[calc(100vh-1.5rem)] lg:self-start lg:overflow-y-auto lg:pr-1">
          <section className="rounded-md border border-tcs-border bg-tcs-raised p-3">
            <h3 className="mb-2 text-sm font-semibold text-tcs-text">Recipients</h3>
            <ul className="space-y-1.5">
              {recipients.map((r) => {
                const i = recipients.findIndex((x) => x.recipient_id === r.recipient_id);
                const isActive = activeRecipient === r.recipient_id;
                return (
                  <li key={r.recipient_id}>
                    <button
                      type="button"
                      onClick={() => r.role === 'SIGNER' && setActiveRecipient(r.recipient_id)}
                      className={`flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs ${isActive ? 'bg-tcs-bg-muted' : 'bg-tcs-raised'}`}
                      style={{ borderColor: recipientColor(i) }}
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: recipientColor(i) }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-tcs-text">
                          {r.name ?? r.email}
                        </span>
                        <span className="text-tcs-text-muted">
                          {r.role}{r.role !== 'CC' ? ` · order ${r.signing_order}` : ''} · {r.status}
                        </span>
                      </span>
                      {isActive && isDraft && (
                        <span className="shrink-0 text-[10px] font-semibold" style={{ color: recipientColor(i) }}>
                          placing
                        </span>
                      )}
                    </button>

                    {/* Identity policy per recipient. Opt-in only, per Ben's
                        2026-07-29 decision — no forcing by document type. The
                        help text states the SIGNER-side consequence, because
                        the whole point of the toggle is that it adds a step
                        to their experience, and a sender ticking it should
                        know that before they send. */}
                    {isDraft && r.role !== 'CC' && (
                      <div className="mt-1 pl-2">
                        <label className="flex items-center gap-1.5 text-[11px] text-tcs-text">
                          <input
                            type="checkbox"
                            checked={(r.auth_mode ?? 'emailed_link') === 'email_otp'}
                            disabled={toolsBusy}
                            onChange={async (e) => {
                              try {
                                await setRecipientAuthMode(
                                  r.recipient_id,
                                  e.target.checked ? 'email_otp' : 'emailed_link',
                                );
                                refresh();
                              } catch (err) {
                                setSendError(
                                  errorMessage(err, 'Could not update.'),
                                );
                              }
                            }}
                            className="h-3.5 w-3.5"
                          />
                          Require a one-time code
                        </label>
                        <p className="mt-0.5 text-[10px] leading-snug text-tcs-text-muted">
                          {(r.auth_mode ?? 'emailed_link') === 'email_otp'
                            ? 'This will require the signer to request a 6-digit code by email and enter it before the document opens. Use it when it matters who signs — a subcontractor agreement, say — and accept that it adds a step.'
                            : 'Off: opening the link we email them is treated as proof of identity. Tick this to require a 6-digit emailed code as well.'}
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {isDraft && (
              <div className="mt-3 border-t border-tcs-border pt-3">
                <label htmlFor="rcp-email" className="block text-[11px] font-medium text-tcs-text-secondary">
                  Email *
                </label>
                <input
                  id="rcp-email"
                  type="email"
                  value={rEmail}
                  onChange={(e) => setREmail(e.target.value)}
                  className="mb-1.5 mt-0.5 w-full rounded-md border border-tcs-border px-2 py-1.5 text-xs"
                />
                <label htmlFor="rcp-name" className="block text-[11px] font-medium text-tcs-text-secondary">
                  Name
                </label>
                <input
                  id="rcp-name"
                  value={rName}
                  onChange={(e) => setRName(e.target.value)}
                  className="mb-1.5 mt-0.5 w-full rounded-md border border-tcs-border px-2 py-1.5 text-xs"
                />
                <div className="mb-2 flex gap-2">
                  <div className="flex-1">
                    <label htmlFor="rcp-role" className="block text-[11px] font-medium text-tcs-text-secondary">
                      Role
                    </label>
                    <select
                      id="rcp-role"
                      value={rRole}
                      onChange={(e) => setRRole(e.target.value as RecipientRole)}
                      className="mt-0.5 w-full rounded-md border border-tcs-border px-2 py-1.5 text-xs"
                    >
                      <option value="SIGNER">Signer</option>
                      <option value="APPROVER">Approver</option>
                      <option value="CC">CC (completed copy)</option>
                    </select>
                  </div>
                  {rRole !== 'CC' && (
                    <div className="w-20">
                      <label htmlFor="rcp-order" className="block text-[11px] font-medium text-tcs-text-secondary">
                        Order
                      </label>
                      <input
                        id="rcp-order"
                        type="number"
                        min={1}
                        value={rOrder}
                        onChange={(e) => setROrder(Number(e.target.value) || 1)}
                        className="mt-0.5 w-full rounded-md border border-tcs-border px-2 py-1.5 text-xs"
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={addRecipient}
                  disabled={rBusy || !rEmail.trim()}
                  className="inline-flex w-full items-center justify-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-xs font-medium text-tcs-text disabled:opacity-50"
                >
                  {rBusy
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    : <UserPlus className="h-3.5 w-3.5" aria-hidden />}
                  Add recipient
                </button>
                {rError && <p role="alert" className="mt-1.5 text-[11px] text-st-danger-fg">{rError}</p>}
              </div>
            )}
          </section>

          {/* routing preview */}
          {signersAndApprovers.length > 0 && (
            <section className="rounded-md border border-tcs-border bg-tcs-raised p-3">
              <h3 className="mb-2 text-sm font-semibold text-tcs-text">Routing</h3>
              <ol className="space-y-1.5">
                {orders.map((o) => (
                  <li key={o} className="flex items-start gap-2 text-xs text-tcs-text-secondary">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tcs-surface text-[10px] font-semibold text-tcs-text">
                      {o}
                    </span>
                    <span>
                      {signersAndApprovers
                        .filter((r) => r.signing_order === o)
                        .map((r) => `${r.name ?? r.email}${r.role === 'APPROVER' ? ' (approve)' : ''}`)
                        .join(' + ')}
                      {signersAndApprovers.filter((r) => r.signing_order === o).length > 1
                        ? ' — in parallel' : ''}
                    </span>
                  </li>
                ))}
                {ccs.length > 0 && (
                  <li className="flex items-start gap-2 text-xs text-tcs-text-muted">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tcs-surface text-[10px]">
                      cc
                    </span>
                    <span>{ccs.map((r) => r.name ?? r.email).join(', ')} — completed copy</span>
                  </li>
                )}
              </ol>
            </section>
          )}

          {/* field tools: required toggle, anchor placement, layouts */}
          {isDraft && (
            <FieldTools
              selected={fields.find((f) => f.field_id === selectedField) ?? null}
              fieldCount={fields.length}
              requiredCount={fields.filter((f) => f.required).length}
              templates={templates}
              anchorReady={textRuns.length > 0}
              textError={textError}
              busy={toolsBusy}
              disabled={!isDraft}
              message={toolsMsg}
              onToggleRequired={toggleRequired}
              onRemove={removeSelected}
              onResize={resizeSelected}
              onPlaceByAnchor={placeByAnchor}
              onApplyLayout={doApplyLayout}
              onSaveLayout={doSaveLayout}
              suggestions={suggestions}
              onDetect={runDetection}
              onAcceptSuggestions={acceptSuggestions}
            />
          )}
        </div>

        {/* ── document with overlay ──────────────────────────────────────── */}
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div>
            {isDraft && (
              <div className="sticky top-0 z-10 mb-3 rounded-md border border-tcs-border bg-tcs-raised p-2">
                {placeableRecipients.length === 0 ? (
                  <p className="text-[11px] text-tcs-text-muted">
                    Add a <strong>Signer</strong> before placing fields.
                    Approvers review and approve without signing, and CC only
                    receives the completed copy — neither can hold a field.
                  </p>
                ) : !activeIsPlaceable ? (
                  <p className="text-[11px] text-tcs-text-muted">
                    Pick a signer on the left to place fields for.
                  </p>
                ) : (
                  <>
                    <p className="mb-1.5 text-[11px] text-tcs-text-muted">
                      Drag a field onto the document for the highlighted signer.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {FIELD_TYPES.map((t) => (
                        <PaletteChip
                          key={t}
                          type={t}
                          color={recipientColor(
                            recipients.findIndex((r) => r.recipient_id === activeRecipient),
                          )}
                        />
                      ))}
                    </div>
                  </>
                )}
                {/* Per-field controls live in the left rail (FieldTools) so
                    the required/optional toggle sits next to the anchor and
                    layout tools it is used alongside. */}
              </div>
            )}

            {pdfError && <p className="text-sm text-st-danger-fg">{pdfError}</p>}
            {pages.length === 0 && !pdfError && (
              <div className="flex h-64 items-center justify-center rounded-md border border-dashed border-tcs-border text-sm text-tcs-text-muted">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> Rendering document…
              </div>
            )}

            {pages.map((p) => (
              <PdfPage key={p.pageNumber} page={p} registerRef={registerRef}>
                {fields
                  .filter((f) => f.page === p.pageNumber)
                  .map((f) => {
                    const i = recipients.findIndex(
                      (r) => r.recipient_id === f.recipient_id,
                    );
                    return (
                      <FieldBox
                        key={f.field_id}
                        field={f}
                        color={recipientColor(i)}
                        pageW={p.width}
                        pageH={p.height}
                        selected={selectedField === f.field_id}
                        editable={isDraft}
                        onSelect={() => setSelectedField(f.field_id)}
                      />
                    );
                  })}
              </PdfPage>
            ))}
          </div>
        </DndContext>
      </div>
    </div>
  );
}
